import React, { useState, useEffect } from 'react';
import { Translate as t } from '../../config/Translate';
import { ConfirmDialog } from '../common/ConfirmDialog';
import {
	timeSlotsToStartEndBreaks,
	startEndBreaksToTimeSlots,
	COMMON_TIMEZONES,
	getTimezoneLabel,
} from './workScheduleUtils';

const DAY_KEYS = ['common.day.sun', 'common.day.mon', 'common.day.tue', 'common.day.wed', 'common.day.thu', 'common.day.fri', 'common.day.sat'];

const WorkScheduleBaseEditor = ({ show, onClose, effectiveBase, onSave, onReset, isLoading, userTimezone, companyTimezone }) => {
	const [workDays, setWorkDays] = useState([1, 2, 3, 4, 5]);
	const [workStart, setWorkStart] = useState('09:00');
	const [workEnd, setWorkEnd] = useState('18:00');
	const [breaks, setBreaks] = useState([{ start: '12:00', end: '13:00' }]);
	const [timezone, setTimezone] = useState('');

	useEffect(() => {
		if (show && effectiveBase) {
			setWorkDays([...(effectiveBase.workDays || [1, 2, 3, 4, 5])]);
			const parsed = timeSlotsToStartEndBreaks(effectiveBase.timeSlots);
			setWorkStart(parsed.workStart);
			setWorkEnd(parsed.workEnd);
			setBreaks(parsed.breaks.length > 0 ? parsed.breaks : []);
			// 개인 timezone만 표시 (조직과 같거나 미설정이면 '' = "Same as company")
			setTimezone(userTimezone || '');
		}
	}, [show, effectiveBase, userTimezone]);

	const toggleDay = (dayIdx) => {
		setWorkDays(prev =>
			prev.includes(dayIdx) ? prev.filter(d => d !== dayIdx) : [...prev, dayIdx].sort()
		);
	};

	const addBreak = () => {
		setBreaks(prev => [...prev, { start: '12:00', end: '13:00' }]);
	};

	const removeBreak = (idx) => {
		setBreaks(prev => prev.filter((_, i) => i !== idx));
	};

	const updateBreak = (idx, field, value) => {
		setBreaks(prev => prev.map((b, i) => i === idx ? { ...b, [field]: value } : b));
	};

	const handleSave = () => {
		const timeSlots = startEndBreaksToTimeSlots(workStart, workEnd, breaks);
		onSave({ workDays, timeSlots, timezone });
	};

	// window.confirm 대체: 버튼은 확인 대화상자만 열고, 확인 시 onReset 실행
	const [confirmReset, setConfirmReset] = useState(false);
	const handleReset = () => setConfirmReset(true);

	if (!show) return null;

	return (
		<div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
			<div className="modal-dialog">
				<div className="modal-content">
					<div className="modal-header">
						<h5 className="modal-title">{t('user.workSchedule.editBase')}</h5>
						<button type="button" className="btn-close" onClick={onClose} disabled={isLoading}></button>
					</div>
					<div className="modal-body">
						{/* Timezone */}
						<div className="mb-3">
							<label className="form-label fw-bold">{t('user.workSchedule.personalTimezone')}</label>
							{companyTimezone && (
								<div className="text-muted small mb-1">
									{t('user.workSchedule.companyTimezone')}: <strong>{getTimezoneLabel(companyTimezone)}</strong>
								</div>
							)}
							<select
								className="form-select form-select-sm"
								value={timezone}
								onChange={(e) => setTimezone(e.target.value)}
							>
								<option value="">{t('user.workSchedule.sameAsCompany')}{companyTimezone ? ` (${getTimezoneLabel(companyTimezone)})` : ''}</option>
								{COMMON_TIMEZONES.map(tz => (
									<option key={tz.value} value={tz.value}>{tz.label}</option>
								))}
							</select>
						</div>

						{/* Work Days */}
						<div className="mb-3">
							<label className="form-label fw-bold">{t('user.workSchedule.workDays')}</label>
							<div className="d-flex flex-wrap gap-2">
								{DAY_KEYS.map((key, idx) => (
									<button
										key={idx}
										type="button"
										className={`btn btn-sm ${workDays.includes(idx) ? 'btn-primary' : 'btn-outline-secondary'}`}
										onClick={() => toggleDay(idx)}
									>
										{t(key)}
									</button>
								))}
							</div>
						</div>

						{/* Work Hours */}
						<div className="mb-3">
							<label className="form-label fw-bold">{t('user.workSchedule.timeSlots')}</label>
							<div className="row g-2">
								<div className="col-6">
									<input
										type="time"
										className="form-control"
										value={workStart}
										onChange={(e) => setWorkStart(e.target.value)}
									/>
								</div>
								<div className="col-6">
									<input
										type="time"
										className="form-control"
										value={workEnd}
										onChange={(e) => setWorkEnd(e.target.value)}
									/>
								</div>
							</div>
						</div>

						{/* Breaks */}
						<div className="mb-3">
							<label className="form-label fw-bold">Break</label>
							{breaks.map((brk, idx) => (
								<div key={idx} className="row g-2 mb-2 align-items-center">
									<div className="col-5">
										<input
											type="time"
											className="form-control form-control-sm"
											value={brk.start}
											onChange={(e) => updateBreak(idx, 'start', e.target.value)}
										/>
									</div>
									<div className="col-5">
										<input
											type="time"
											className="form-control form-control-sm"
											value={brk.end}
											onChange={(e) => updateBreak(idx, 'end', e.target.value)}
										/>
									</div>
									<div className="col-2">
										<button
											type="button"
											className="btn btn-sm btn-outline-danger"
											onClick={() => removeBreak(idx)}
										>
											<ion-icon name="close-outline"></ion-icon>
										</button>
									</div>
								</div>
							))}
							<button type="button" className="btn btn-sm btn-outline-primary" onClick={addBreak}>
								+ {t('user.workSchedule.addTimeSlot')}
							</button>
						</div>
					</div>
					<div className="modal-footer">
						<button
							type="button"
							className="btn btn-outline-warning btn-sm"
							onClick={handleReset}
							disabled={isLoading}
						>
							{t('user.workSchedule.resetToDefault')}
						</button>
						<button type="button" className="btn btn-secondary btn-sm" onClick={onClose} disabled={isLoading}>
							{t('common.button.cancel')}
						</button>
						<button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={isLoading}>
							{isLoading ? t('service.issueManager.processing') : t('common.button.save')}
						</button>
					</div>
				</div>
			</div>
			<ConfirmDialog
				show={confirmReset}
				onClose={() => setConfirmReset(false)}
				onConfirm={() => onReset && onReset()}
				title={t('common.message.confirm.areYouSure')}
				message={t('user.workSchedule.resetToDefault') + '?'}
				confirmLabel={t('user.workSchedule.resetToDefault')}
				confirmCss="btn btn-block btn-warning" />
		</div>
	);
};

export default WorkScheduleBaseEditor;
